import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router';
import { create } from 'zustand';
import { qk, type Tenancy, type Unit } from '@immoaktas/api-client';
import { api } from '../lib/api';
import { scopeOfPath, type PathScope, type PropertySection } from './scope';

const KEY = 'ia:scope';
const read = () => {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
};
/** Last property the landlord worked in: neutral pages (Mitteilungen, Fristen …) keep its sidebar. Per browser tab. */
const useLastProperty = create<{ id: string | null; set: (id: string | null) => void }>((set) => ({
  id: read(),
  set: (id) => {
    try {
      if (id) sessionStorage.setItem(KEY, id);
      else sessionStorage.removeItem(KEY);
    } catch {
      /* private mode: memory only */
    }
    set({ id });
  },
}));

export type Scope = {
  propertyId: string | null;
  section: PropertySection | null;
  path: PathScope;
  /** level 3: the loaded unit and, inside a tenancy, the loaded tenancy (for the trail) */
  unit?: Unit;
  tenancy?: Tenancy;
};

/** Last unit opened per property ("Einheiten › WE 1" stays in the sidebar while working elsewhere in the house). */
const UNIT_KEY = 'ia:lastUnit';
type LastUnits = Record<string, { id: string; label: string }>;
const readUnits = (): LastUnits => {
  try {
    return JSON.parse(sessionStorage.getItem(UNIT_KEY) ?? '{}') as LastUnits;
  } catch {
    return {};
  }
};
export const useLastUnit = create<{
  byProperty: LastUnits;
  set: (propertyId: string, unit: { id: string; label: string } | null) => void;
}>((set, get) => ({
  byProperty: readUnits(),
  set: (propertyId, unit) => {
    const next = { ...get().byProperty };
    if (unit) next[propertyId] = unit;
    else delete next[propertyId];
    try {
      sessionStorage.setItem(UNIT_KEY, JSON.stringify(next));
    } catch {
      /* private mode: memory only */
    }
    set({ byProperty: next });
  },
}));
/** Forget the remembered unit of a property (unit deleted). */
export const forgetLastUnit = (propertyId: string) => useLastUnit.getState().set(propertyId, null);

/**
 * Property the current page belongs to: from the URL (/objekte/:id/…), from the loaded unit, tenancy or period
 * (same query keys as their pages, so nothing is fetched twice), null on the account-wide lists ("Alle Objekte"),
 * the last property on neutral pages.
 */
export function usePropertyScope(): Scope {
  const { pathname } = useLocation();
  const path = scopeOfPath(pathname);
  const entity = path.kind === 'entity' ? path : null;
  const unitPath = path.kind === 'unit' ? path : null;
  const unit = useQuery({
    queryKey: qk.unit(unitPath?.unitId ?? ''),
    queryFn: () => api.units.get(unitPath!.unitId),
    enabled: !!unitPath,
  });
  const tenancyId = entity?.entity === 'tenancy' ? entity.id : (unitPath?.tenancyId ?? '');
  const tenancy = useQuery({
    queryKey: qk.tenancy(tenancyId),
    queryFn: () => api.tenancies.get(tenancyId),
    enabled: !!tenancyId,
  });
  const period = useQuery({
    queryKey: qk.period(entity?.id ?? ''),
    queryFn: () => api.billing.period(entity!.id),
    enabled: entity?.entity === 'period',
  });
  const last = useLastProperty();
  const fromEntity = unitPath
    ? (unit.data?.propertyId ?? null)
    : entity
      ? ((entity.entity === 'tenancy' ? tenancy.data?.propertyId : period.data?.propertyId) ?? null)
      : null;
  const resolved =
    path.kind === 'property'
      ? path.propertyId
      : path.kind === 'entity' || path.kind === 'unit'
        ? (fromEntity ?? last.id)
        : path.kind === 'all'
          ? null
          : last.id;
  const remember =
    path.kind === 'property'
      ? path.propertyId
      : path.kind === 'entity' || path.kind === 'unit'
        ? fromEntity
        : path.kind === 'all'
          ? null
          : undefined;
  const lastUnit = useLastUnit();
  const u = unitPath ? unit.data : undefined;
  useEffect(() => {
    if (!u) return;
    const prev = lastUnit.byProperty[u.propertyId];
    if (prev?.id !== u.id || prev.label !== u.label) lastUnit.set(u.propertyId, { id: u.id, label: u.label });
  }, [u, lastUnit]);
  useEffect(() => {
    if (remember !== undefined && (remember !== null || path.kind === 'all') && remember !== last.id)
      last.set(remember);
  }, [remember, path.kind, last]);
  return {
    propertyId: resolved,
    section: path.kind === 'neutral' ? null : path.section,
    path,
    unit: u,
    tenancy: unitPath?.tenancyId ? tenancy.data : undefined,
  };
}

/** Forget the remembered property (account switch, "Alle Objekte"). */
export const forgetPropertyScope = () => useLastProperty.getState().set(null);
